// src/features/wbs/components/overview/ApprovalQueueModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { approveBatch, rejectBatch } from 'src/lib/wbs/progressService';
import type { WeekFlag } from '../../types';

type Props = {
    open: boolean;
    onClose: () => void;
    projectId: string;
    friday: Date;
    rows: Array<{ id: string; code: string; name: string }>;
    flags: Record<string, WeekFlag>;
    canApprove?: boolean;
    approveEnabled?: boolean;
    approvalOpenAt?: Date | null;
    projectTimeZone?: string;
};

export default function ApprovalQueueModal({
    open,
    onClose,
    projectId,
    friday,
    rows,
    flags,
    canApprove,
    approveEnabled = true,
    approvalOpenAt = null,
    projectTimeZone = 'Asia/Seoul',
}: Props) {
    const codeCollator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);

    const pending = useMemo(() => {
        const list = rows
            .map((r) => {
                const flag = flags[r.id];
                const usePreviousPending = flag?.previousWeekStatus === 'PENDING' && !!flag.previousWeekProgressId;
                return {
                    ...r,
                    flag,
                    queueProgressId: usePreviousPending ? flag?.previousWeekProgressId : flag?.progressId,
                    queueDraftEV: usePreviousPending ? flag?.previousWeekDraftEV : flag?.draftEV,
                    queueWeekOf: usePreviousPending ? flag?.previousWeekWeekOf : null,
                    queueLabel: usePreviousPending ? '전주 미승인' : '금주 제출',
                };
            })
            .filter((r) => {
                if (r.flag?.previousWeekStatus === 'PENDING' && r.queueProgressId) return true;
                return r.flag?.statusForWeek === 'PENDING' && r.queueProgressId != null;
            });

        list.sort((a, b) => {
            const ac = (a.code ?? '').trim();
            const bc = (b.code ?? '').trim();
            const c = codeCollator.compare(ac, bc);
            if (c !== 0) return c;
            return codeCollator.compare((a.name ?? '').trim(), (b.name ?? '').trim());
        });

        return list;
    }, [rows, flags, codeCollator]);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [expected, setExpected] = useState<Record<string, string>>({});
    const [reasons, setReasons] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const approvalBlockedMessage = useMemo(() => {
        if (approveEnabled) return null;
        if (!approvalOpenAt) return '현재 프로젝트는 입력 마감 이후에만 승인할 수 있습니다.';
        const when = new Intl.DateTimeFormat('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: projectTimeZone,
        }).format(approvalOpenAt);
        return `현재 프로젝트는 입력 마감 이후에만 승인할 수 있습니다. 승인 가능 시각: ${when}`;
    }, [approveEnabled, approvalOpenAt, projectTimeZone]);

    useEffect(() => {
        if (!open) return;

        const expectedBuf: Record<string, string> = {};
        const reasonBuf: Record<string, string> = {};

        for (const p of pending) {
            const progressId = p.queueProgressId;
            if (!progressId) continue;

            expectedBuf[progressId] = p.queueDraftEV != null ? Number(p.queueDraftEV).toFixed(1) : '';
            reasonBuf[progressId] = '';
        }

        setExpected(expectedBuf);
        setReasons(reasonBuf);
        setSelected(new Set());
        setError(null);
    }, [open, pending]);

    const selectedItems = useMemo(() => {
        return pending.filter((p) => {
            const progressId = p.queueProgressId;
            return !!progressId && selected.has(progressId);
        });
    }, [pending, selected]);

    const selectedCount = selectedItems.length;
    const allChecked = pending.length > 0 && selected.size === pending.length;

    const hasAnySelectedReason = useMemo(() => {
        return selectedItems.some((p) => {
            const progressId = p.flag?.progressId;
            if (!progressId) return false;
            return (reasons[progressId] ?? '').trim().length > 0;
        });
    }, [selectedItems, reasons]);

    const toggle = (progressId: string, checked: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(progressId);
            else next.delete(progressId);
            return next;
        });
    };

    const toggleAll = (checked: boolean) => {
        if (!checked) {
            setSelected(new Set());
            return;
        }
        setSelected(new Set(pending.map((p) => p.queueProgressId!).filter(Boolean)));
    };

    const onApprove = async () => {
        if (selectedCount === 0 || busy) return;
        if (!approveEnabled) {
            setError(approvalBlockedMessage ?? '현재 프로젝트는 입력 마감 이후에만 승인할 수 있습니다.');
            return;
        }

        if (hasAnySelectedReason) {
            setError('반려 사유가 입력된 항목이 있습니다. 승인 전에 반려 사유를 지우거나, 선택 반려를 사용하세요.');
            return;
        }

        const ok = window.confirm(`선택한 ${selectedCount}건을 승인하시겠습니까?`);
        if (!ok) return;

        setError(null);
        setBusy(true);
        try {
            await approveBatch(projectId, Array.from(selected));
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message ?? e?.message ?? '승인 중 오류가 발생했습니다.');
        } finally {
            setBusy(false);
        }
    };

    const onReject = async () => {
        if (selectedCount === 0 || busy) return;

        const items = Array.from(selected).map((submissionId) => ({
            submissionId,
            expectedEarnedValue: Math.round(Number(expected[submissionId] ?? NaN) * 10) / 10,
            reason: (reasons[submissionId] ?? '').trim() || undefined,
        }));

        if (items.some((x) => Number.isNaN(x.expectedEarnedValue))) {
            setError('반려 시 지정 EV가 비어 있거나 숫자가 아닌 항목이 있습니다.');
            return;
        }

        const ok = window.confirm(`선택한 ${selectedCount}건을 반려하시겠습니까?`);
        if (!ok) return;

        setError(null);
        setBusy(true);
        try {
            await rejectBatch(projectId, items);
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message ?? e?.message ?? '반려 중 오류가 발생했습니다.');
        } finally {
            setBusy(false);
        }
    };

    if (!open || !canApprove) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-6xl rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b px-5 py-4">
                    <div>
                        <div className="text-base font-semibold">승인/반려 대기 목록</div>
                        <div className="mt-1 text-xs text-gray-500">
                            대기 {pending.length}건 · 기준일 {friday.toLocaleDateString()}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
                        onClick={onClose}
                        disabled={busy}
                    >
                        닫기
                    </button>
                </div>

                <div className="px-5 py-4">
                    {approvalBlockedMessage && (
                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {approvalBlockedMessage}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                            {error}
                        </div>
                    )}

                    {pending.length === 0 ? (
                        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-gray-500">
                            대기 중인 제출이 없습니다.
                        </div>
                    ) : (
                        <>
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 text-xs">
                                    <label className="inline-flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="accent-gray-700"
                                            checked={allChecked}
                                            onChange={(e) => toggleAll(e.target.checked)}
                                            disabled={busy}
                                        />
                                        전체 선택
                                    </label>
                                    <span className="text-gray-500">선택: {selectedCount}건</span>
                                </div>
                            </div>

                            <div className="max-h-[58vh] overflow-auto rounded-xl border">
                                <table className="min-w-full table-fixed text-sm">
                                    <colgroup>
                                        <col className="w-12" />
                                        <col className="w-28" />
                                        <col />
                                        <col className="w-28" />
                                        <col className="w-32" />
                                        <col className="w-64" />
                                        <col className="w-28" />
                                    </colgroup>

                                    <thead className="sticky top-0 z-10 bg-gray-50 text-gray-500">
                                        <tr>
                                            <th className="px-3 py-2 text-left"></th>
                                            <th className="px-3 py-2 text-left">코드</th>
                                            <th className="px-3 py-2 text-left">항목 이름</th>
                                            <th className="px-3 py-2 text-right">제출 EV</th>
                                            <th className="px-3 py-2 text-left">반려 시 지정 EV</th>
                                            <th className="px-3 py-2 text-left">반려 사유</th>
                                            <th className="px-3 py-2 text-left">상태</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {pending.map((p) => {
                                            const progressId = p.queueProgressId!;
                                            return (
                                                <tr key={progressId} className="border-t align-middle">
                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="checkbox"
                                                            className="accent-gray-700"
                                                            checked={selected.has(progressId)}
                                                            onChange={(e) => toggle(progressId, e.target.checked)}
                                                            disabled={busy}
                                                        />
                                                    </td>

                                                    <td className="px-3 py-2 font-mono text-xs">{p.code}</td>

                                                    <td className="px-3 py-2">
                                                        <div className="truncate" title={p.name}>
                                                            {p.name}
                                                            {p.queueWeekOf && (
                                                                <div className="mt-1 text-[11px] text-amber-700">
                                                                    {p.queueLabel} · {p.queueWeekOf}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="px-3 py-2 text-right">
                                                        {p.queueDraftEV != null
                                                            ? `${Number(p.queueDraftEV).toFixed(1)}%`
                                                            : '-'}
                                                    </td>

                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            step={0.1}
                                                            className="w-24 rounded border px-2 py-1 text-xs"
                                                            value={expected[progressId] ?? ''}
                                                            onChange={(e) =>
                                                                setExpected((prev) => ({
                                                                    ...prev,
                                                                    [progressId]: e.target.value,
                                                                }))
                                                            }
                                                            disabled={busy}
                                                            placeholder="EV %"
                                                        />
                                                    </td>

                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="text"
                                                            className="w-full rounded border px-2 py-1 text-xs"
                                                            value={reasons[progressId] ?? ''}
                                                            onChange={(e) =>
                                                                setReasons((prev) => ({
                                                                    ...prev,
                                                                    [progressId]: e.target.value,
                                                                }))
                                                            }
                                                            disabled={busy}
                                                            placeholder="반려 사유"
                                                        />
                                                    </td>

                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-col gap-1">
                                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                                                                PENDING
                                                            </span>
                                                            {p.queueWeekOf && (
                                                                <span className="text-[11px] text-gray-500">{p.queueWeekOf}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-4 flex items-center gap-2">
                                <button
                                    type="button"
                                    className="rounded-lg bg-gray-900 px-3 py-2 text-xs text-white hover:bg-gray-700 disabled:opacity-50"
                                    onClick={onApprove}
                                    disabled={busy || selectedCount === 0 || !approveEnabled}
                                >
                                    선택 승인
                                </button>

                                <button
                                    type="button"
                                    className="rounded-lg border border-rose-300 px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                    onClick={onReject}
                                    disabled={busy || selectedCount === 0}
                                >
                                    선택 반려
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
